import { useState, useCallback, useEffect, useMemo } from "react";
import { usePaymentsUIContext } from "../../../../../../context/PaymentsUI/context/PaymentsUI/context.js";
import { PaymentFlow } from "../../../../../../models/payment/models/payment/types.js";
const useQrPaymentState = ({
  paymentState,
  orderId,
  error,
  paymentResponse,
  orderToken,
  onQrCodeRefresh,
  refetch,
  paymentFlow,
  cart,
  getNewCart
}) => {
  const [isQrCodeExpired, setIsQrCodeExpired] = useState(false);
  const [isQrRefreshLoading, setIsQrRefreshLoading] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [qrCodeExpireIn, setQrCodeExpireIn] = useState(0);
  const [externalUrl, setExternalUrl] = useState("");
  const { setPaymentQRCodeState, isQrCodeModalOpen, setIsQrCodeModalOpen } = usePaymentsUIContext();
  const cancelPayment = useCallback(async () => {
    if (!cart?.id) {
      console.error("Need cart id to recover");
      setIsQrCodeModalOpen(false);
      return;
    }
    try {
      const cartResponse = await getNewCart(cart?.id);
      if (!cartResponse.success) {
        throw "Failed to recreate cart";
      }
      cartResponse.cartId && refetch(cartResponse.cartId, cartResponse.cartHash);
    } catch (e) {
      console.error("Error:", e);
    }
    setIsQrCodeModalOpen(false);
  }, [cart?.id, getNewCart, refetch, setIsQrCodeModalOpen]);
  useEffect(() => {
    if (paymentState) {
      setPaymentQRCodeState(paymentState);
    }
    if (paymentState === "Failed" || error) {
      cancelPayment();
    }
  }, [paymentState, error, cancelPayment, orderId, orderToken, setPaymentQRCodeState]);
  useEffect(() => {
    if (qrCodeExpireIn) {
      const timeoutId = setTimeout(() => {
        setIsQrCodeExpired(true);
        setQrCodeExpireIn(0);
      }, qrCodeExpireIn);
      return () => clearTimeout(timeoutId);
    }
  }, [qrCodeExpireIn]);
  useEffect(() => {
    setQrCodeExpireIn(Number(paymentResponse.qrCodeExpiresIn) * 1e3);
    setQrCode(String(paymentResponse.qrCodeUrl));
    setExternalUrl(String(paymentResponse.redirectUrl));
    setIsQrCodeExpired(false);
    setIsQrRefreshLoading(false);
  }, [paymentResponse.qrCodeExpiresIn, paymentResponse.qrCodeUrl, paymentResponse.redirectUrl]);
  useEffect(() => {
    if (paymentFlow === PaymentFlow.QR_CODE) {
      setIsQrCodeModalOpen(true);
    }
  }, [paymentFlow, setIsQrCodeModalOpen]);
  const handleRefreshQrCode = useCallback(async () => {
    if (cart && orderId) {
      setQrCodeExpireIn(0);
      setIsQrCodeExpired(true);
      await getNewCart(orderId);
      onQrCodeRefresh();
    }
    setIsQrRefreshLoading(true);
  }, [cart, onQrCodeRefresh, orderId, getNewCart]);
  return useMemo(
    () => ({
      externalUrl,
      isOpen: isQrCodeModalOpen,
      isQrCodeExpired,
      isQrRefreshLoading,
      onCancel: () => cancelPayment(),
      qrCodeUrl: qrCode,
      refreshQrCode: () => handleRefreshQrCode()
    }),
    [
      cancelPayment,
      externalUrl,
      handleRefreshQrCode,
      isQrCodeExpired,
      isQrCodeModalOpen,
      isQrRefreshLoading,
      qrCode
    ]
  );
};
export {
  useQrPaymentState
};
//# sourceMappingURL=use-qr-payment-state.js.map
